import { useEffect, useRef, useState } from 'react'
import { Superpose } from '../components/Superpose'

/**
 * Appareil photo qui reste ouvert entre les déclenchements.
 *
 * C'est la décision d'ergonomie no 6 : sur un mur de 30 mètres, revenir à la
 * grille entre chaque prise est intenable. Les notes ne sont pas demandées
 * ici — elles se posent ensuite, depuis la grille.
 *
 * Zoom (28 octobre 2026) : boutons 0,5x / 1x / 2x / 3x… selon ce que le
 * téléphone déclare. Sur Android (Chrome), le zoom passe par la capacité
 * « zoom » de la caméra ; sur les téléphones à plusieurs objectifs, Chrome
 * bascule lui-même vers le grand-angle ou le téléobjectif. Si le téléphone
 * ne déclare aucun zoom mais expose plusieurs caméras arrière, un bouton
 * « Objectif » permet de passer de l'une à l'autre.
 */

type Zoom = { min: number; max: number }

const PALIERS = [0.5, 1, 2, 3, 5, 10]

function libelle(z: number) {
  return `${String(z).replace('.', ',')}x`
}

export function CameraRafale({
  fermer,
  capturer
}: {
  fermer: () => void
  capturer: (fichiers: Blob[]) => void
}) {
  const video = useRef<HTMLVideoElement>(null)
  const flux = useRef<MediaStream | null>(null)
  const [prises, setPrises] = useState<{ blob: Blob; url: string }[]>([])
  const [erreur, setErreur] = useState<string | null>(null)
  const [pret, setPret] = useState(false)

  const [zoomDispo, setZoomDispo] = useState<Zoom | null>(null)
  const [zoom, setZoom] = useState(1)
  const [objectifs, setObjectifs] = useState<string[]>([])
  const [objectif, setObjectif] = useState<string | null>(null)

  useEffect(() => {
    let annule = false

    async function ouvrir() {
      const base: MediaTrackConstraints = objectif
        ? { deviceId: { exact: objectif }, width: { ideal: 2560 } }
        : { facingMode: { ideal: 'environment' }, width: { ideal: 2560 } }

      let s: MediaStream
      try {
        // Chrome n'expose la capacité « zoom » que si on la demande.
        s = await navigator.mediaDevices.getUserMedia({
          video: { ...base, zoom: true } as MediaTrackConstraints,
          audio: false
        })
      } catch {
        s = await navigator.mediaDevices.getUserMedia({ video: base, audio: false })
      }
      if (annule) {
        s.getTracks().forEach((t) => t.stop())
        return
      }
      flux.current?.getTracks().forEach((t) => t.stop())
      flux.current = s
      if (video.current) {
        video.current.srcObject = s
        void video.current.play()
      }

      const piste = s.getVideoTracks()[0]
      const caps = (piste?.getCapabilities?.() ?? {}) as MediaTrackCapabilities & {
        zoom?: { min: number; max: number }
      }
      if (caps.zoom && caps.zoom.max > caps.zoom.min) {
        setZoomDispo({ min: caps.zoom.min, max: caps.zoom.max })
        const depart = Math.min(Math.max(1, caps.zoom.min), caps.zoom.max)
        await appliquerZoom(piste, depart)
        if (!annule) setZoom(depart)
      } else {
        setZoomDispo(null)
      }

      // Caméras arrière disponibles (les noms ne sont lisibles qu'une fois
      // l'autorisation donnée).
      try {
        const appareils = await navigator.mediaDevices.enumerateDevices()
        const arriere = appareils
          .filter((a) => a.kind === 'videoinput' && /back|arri[eè]re|rear|environment/i.test(a.label))
          .map((a) => a.deviceId)
        if (!annule) setObjectifs(arriere)
      } catch {
        /* sans importance */
      }

      if (!annule) setPret(true)
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setErreur("Cet appareil ne permet pas d'ouvrir l'appareil photo dans le navigateur.")
      return
    }
    setPret(false)
    ouvrir().catch(() => {
      if (!annule)
        setErreur(
          "Impossible d'ouvrir l'appareil photo. Vérifie l'autorisation dans les réglages du navigateur."
        )
    })
    return () => {
      annule = true
    }
  }, [objectif])

  // Fermeture de l'écran : on libère la caméra.
  useEffect(() => () => flux.current?.getTracks().forEach((t) => t.stop()), [])

  const prisesRef = useRef(prises)
  prisesRef.current = prises
  useEffect(
    () => () => prisesRef.current.forEach((p) => URL.revokeObjectURL(p.url)),
    []
  )

  async function appliquerZoom(piste: MediaStreamTrack | undefined, z: number) {
    if (!piste) return
    try {
      await piste.applyConstraints({ advanced: [{ zoom: z } as MediaTrackConstraintSet] })
    } catch {
      /* zoom refusé par l'appareil : on reste où on est */
    }
  }

  async function choisirZoom(z: number) {
    await appliquerZoom(flux.current?.getVideoTracks()[0], z)
    setZoom(z)
  }

  function objectifSuivant() {
    if (objectifs.length < 2) return
    const i = objectif ? objectifs.indexOf(objectif) : 0
    setObjectif(objectifs[(i + 1) % objectifs.length])
  }

  const paliers = zoomDispo
    ? PALIERS.filter((z) => z >= zoomDispo.min - 0.01 && z <= zoomDispo.max + 0.01)
    : []
  // Si le téléphone ne descend pas sous 1x mais commence un peu au-dessus,
  // on garde quand même un bouton pour le minimum.
  if (zoomDispo && paliers.length && paliers[0] > zoomDispo.min + 0.01 && zoomDispo.min < 1) {
    paliers.unshift(Math.round(zoomDispo.min * 10) / 10)
  }
  const montrerZoom = paliers.length >= 2
  const montrerObjectif = !zoomDispo && objectifs.length >= 2

  async function declencher() {
    const v = video.current
    if (!v || !v.videoWidth) return
    const toile = document.createElement('canvas')
    toile.width = v.videoWidth
    toile.height = v.videoHeight
    const ctx = toile.getContext('2d')
    if (!ctx) return
    ctx.drawImage(v, 0, 0)
    const blob = await new Promise<Blob | null>((r) => toile.toBlob(r, 'image/jpeg', 0.92))
    if (!blob) return
    setPrises((p) => [...p, { blob, url: URL.createObjectURL(blob) }])
    if (navigator.vibrate) navigator.vibrate(18)
  }

  function terminer() {
    capturer(prises.map((p) => p.blob))
  }

  return (
    <Superpose className="camera">
      <div className="camera-vue">
        {erreur ? (
          <div className="camera-erreur">
            <p>{erreur}</p>
            <button className="bouton creux" onClick={fermer} style={{ marginTop: 14 }}>
              Revenir
            </button>
          </div>
        ) : (
          <video ref={video} playsInline muted />
        )}

        {!erreur && (
          <div className="camera-bas">
            {montrerZoom && (
              <div className="camera-zoom" role="group" aria-label="Zoom">
                {paliers.map((z) => (
                  <button
                    key={z}
                    className={Math.abs(z - zoom) < 0.01 ? 'actif' : ''}
                    onClick={() => void choisirZoom(z)}
                    aria-pressed={Math.abs(z - zoom) < 0.01}
                  >
                    {libelle(z)}
                  </button>
                ))}
              </div>
            )}
            {montrerObjectif && (
              <div className="camera-zoom">
                <button className="actif" onClick={objectifSuivant}>
                  Objectif {(objectif ? objectifs.indexOf(objectif) : 0) + 1}/{objectifs.length}
                </button>
              </div>
            )}
            {prises.length > 0 && (
              <div className="camera-bande">
                {prises.map((p, i) => (
                  <img key={i} src={p.url} alt={`Prise ${i + 1}`} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="camera-barre">
        <button className="camera-txt" onClick={fermer}>
          Annuler
        </button>
        <button
          className="camera-declencheur"
          onClick={() => void declencher()}
          disabled={!pret || !!erreur}
          aria-label="Prendre une photo"
        >
          <span />
        </button>
        <button className="camera-txt fort" onClick={terminer} disabled={prises.length === 0}>
          Terminé{prises.length > 0 ? ` · ${prises.length}` : ''}
        </button>
      </div>
    </Superpose>
  )
}
